import { IncidentModel } from '@app/models/incident.model';
import { RimsStepProperty } from '@app/models/rims.model';
import { MergeColumn } from './merge-column.model';
import { TopHeaderColumn } from './merge-header-column.model';

export type IncidentRootFieldName = Extract<keyof IncidentModel, string>;
export type IncidentEntityFieldName = Extract<keyof NonNullable<IncidentModel['entity']>, string>;
export type IncidentActorFieldName = Extract<keyof NonNullable<IncidentModel['actor']>, string>;
export type IncidentCustomFieldName = `custom.${string}`;
export type IncidentRimsFieldName = `rims.${string}`;

// Every merge view row is associated with a field in the incident model. The field name is represented as a string, and it can be a root-level field, a nested field under 'entity' or 'actor', or a custom field under 'custom' or 'rims'.
// The 'custom' fields need special type of handling because their value is not directly taken from the IncidentModel. it's usually formatted differently. Example is the time span of attack which consists of 2 incidentModel fields - start and the end date.
export type IncidentModelFieldName =
  | IncidentRootFieldName
  | `entity.${IncidentEntityFieldName}`
  | `actor.${IncidentActorFieldName}`
  | IncidentCustomFieldName
  | IncidentRimsFieldName;

// Merge view rows bundle

export type MergeViewRows = {
  topHeaderColumns: TopHeaderColumn[];
  detailsRows: MergeRow[];
  entityRows: MergeRow[];
  threatActorRows: MergeRow[];
  articleReferenceRows: ReferenceRow[];
  mediaReferenceRows: ReferenceRow[];
  rimsPhaseRows: RimsPhaseRow[];
};

// Merge row related

export interface MergeRowBase {
  displayName: string;
  field?: IncidentModelFieldName;
  description?: string;
  type: RowType;
  hasConflict: boolean;
  isExpanded?: boolean;
  depth?: number;
}

export type RowType = 'text' | 'text-expandable' | 'media' | 'article' | 'website' | 'slide-toggle' | 'country';

export interface MergeRow extends MergeRowBase {
  columns: MergeColumn[];
}

export interface SingleColumnMergeRow extends MergeRowBase {
  columnIndex: number;
  column: MergeColumn;
}

// A reference row (article or media) is either grouped across matching incidents (MergeRow) or unmatched (SingleColumnMergeRow).
export type ReferenceRow = MergeRow | SingleColumnMergeRow;

// Maps a row field to the field reference name(s) the merged incident uses to record where the value came from.
export const FIELD_REFERENCE_NAMES_BY_ROW_FIELD: Partial<Record<IncidentModelFieldName, string[]>> = {
  title: ['Title'],
  summary: ['Summary'],
  motivation: ['Motivation'],
  'custom.timeSpanOfAttack': ['StartedAt', 'EndedAt'],
  'custom.entity': ['EntityId'],
  'custom.actor': ['ActorId'],
  'entity.countryCode': ['EntityId'],
};

// RIMS related

export type RimsStepRow = RimsStepFieldRow | RimsStepExpandableRow;

export type RimsStepValueContainer = Record<string, RimsStepProperty>;

export type RimsStepFieldRow = {
  kind: 'field';
  row: MergeRow;
  // Fields nested inside a RIMS set item never show their own merge checkbox; selection happens at the set-item level instead.
  isSetItemField?: boolean;
};

export type RimsStepExpandableRow = {
  kind: 'object' | 'array' | 'array-item';
  displayName: string;
  field: IncidentRimsFieldName;
  depth: number;
  children: RimsStepRow[];
  // Only set when kind is 'array-item'; tracks which incident columns have a matching set item for this row.
  columns?: RimsStepArrayItemColumn[];
  // Only set when kind is 'array-item'; the field of the parent set collection itself (without the item index).
  arrayField?: IncidentRimsFieldName;
  // Only set when kind is 'array-item'; the shared 'id' of the set item this row represents (null when no incident's item had a usable id).
  itemId?: string | null;
};

export type RimsStepArrayItemColumn = {
  incidentId?: number;
  hasValue: boolean;
  isChecked?: boolean;
};

export type RimsPhaseRow = {
  phaseId: string;
  phaseName: string;
  steps: RimsPhaseStepRow[];
};

export type RimsPhaseStepRow = {
  stepId: string;
  stepName: string;
};
